
import React, { useEffect, useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useQuickscan } from '@/contexts/QuickscanContext';
import { useQuickscanSubmission } from '@/hooks/useQuickscanSubmission';
import { usePDFGeneration } from '@/hooks/usePDFGeneration';
import { useProfessionalPDFGeneration } from '@/hooks/useProfessionalPDFGeneration';
import { Euro, Leaf, Calendar, Download, Mail, CheckCircle } from 'lucide-react';
import { EnergyMeter3D } from '../EnergyMeter3D';
import { ComparisonIndicator } from '../ComparisonIndicator';
import { SocialProofWidget } from '../SocialProofWidget';
import { Logo } from '../Logo';
import { useToast } from '@/hooks/use-toast';
import { TREE_CO2_UPTAKE_KG_YEAR } from '@/features/laadplein-designer/lib/methodiek/constants';

// Default HTML template - can be customized later
const DEFAULT_HTML_TEMPLATE = `
<!DOCTYPE html>
<html lang="nl">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Quickscan Rapport - {{companyName}}</title>
    <style>
        body {
            font-family: 'Arial', sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
        }
        .header {
            text-align: center;
            margin-bottom: 40px;
            padding: 20px 0;
            border-bottom: 3px solid #014336;
        }
        .logo {
            font-size: 24px;
            font-weight: bold;
            color: #014336;
            margin-bottom: 10px;
        }
        .company-info {
            background: #f8f9fa;
            padding: 20px;
            border-radius: 8px;
            margin-bottom: 30px;
        }
        .results-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 20px;
            margin-bottom: 30px;
        }
        .result-card {
            background: white;
            border: 2px solid #014336;
            border-radius: 8px;
            padding: 20px;
            text-align: center;
        }
        .result-value {
            font-size: 32px;
            font-weight: bold;
            color: #014336;
            margin-bottom: 10px;
        }
        .result-label {
            font-size: 14px;
            color: #666;
        }
        .recommendations {
            background: #f0f8f0;
            padding: 20px;
            border-radius: 8px;
            margin-bottom: 30px;
        }
        .recommendations h3 {
            color: #014336;
            margin-bottom: 15px;
        }
        .recommendations ul {
            list-style-type: none;
            padding: 0;
        }
        .recommendations li {
            background: white;
            margin-bottom: 10px;
            padding: 10px;
            border-radius: 5px;
            border-left: 4px solid #014336;
        }
        .footer {
            text-align: center;
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            font-size: 12px;
            color: #666;
        }
    </style>
</head>
<body>
    <div class="header">
        <div class="logo">Energie Quickscan Rapport</div>
        <h1>{{companyName}}</h1>
        <p>Gegenereerd op {{currentDate}}</p>
    </div>

    <div class="company-info">
        <h2>Bedrijfsgegevens</h2>
        <p><strong>Bedrijfstype:</strong> {{businessType}}</p>
        <p><strong>Aantal werknemers:</strong> {{employeeCount}}</p>
        <p><strong>Maandelijkse energiekosten:</strong> €{{monthlyEnergyCosts}}</p>
        <p><strong>Geschat PV-opwekking:</strong> {{estimatedPvGeneration}} kWh/jaar</p>
        <p><strong>Geschat energieverbruik:</strong> {{estimatedKwhUsage}} kWh/jaar</p>
        <p><strong>Piekuren energiegebruik:</strong> {{energyPeakHours}}</p>
        <p><strong>Huidige oplossingen:</strong> {{currentSolutions}}</p>
    </div>

    <div class="results-grid">
        <div class="result-card">
            <div class="result-value">{{energyPotentialScore}}</div>
            <div class="result-label">Energiepotentieel Score</div>
        </div>
        <div class="result-card">
            <div class="result-value">{{formattedSavings}}</div>
            <div class="result-label">Jaarlijkse Besparing</div>
        </div>
        <div class="result-card">
            <div class="result-value">{{co2Reduction}} ton</div>
            <div class="result-label">CO2 Reductie per jaar</div>
        </div>
        <div class="result-card">
            <div class="result-value">{{roiMonths}} maanden</div>
            <div class="result-label">Terugverdientijd</div>
        </div>
    </div>

    <div class="recommendations">
        <h3>Aanbevelingen voor uw bedrijf</h3>
        <ul>
            {{recommendations}}
        </ul>
    </div>

    <div class="footer">
        <p>Dit rapport is gegenereerd op basis van de opgegeven gegevens tijdens de Energie Quickscan.</p>
        <p>Rapport ID: {{submissionId}}</p>
    </div>
</body>
</html>
`;

export const ResultsScreen = () => {
  const { data, results, calculateResults, submissionId, setSubmissionId } = useQuickscan();
  const { submitQuickscanData, saveContactDetails, isSubmitting, submissionError } = useQuickscanSubmission();
  const { generatePDF, isGenerating, generationError } = usePDFGeneration();
  const { generateProfessionalPDF, isGenerating: isProfessionalGenerating, generationError: professionalError } = useProfessionalPDFGeneration();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [isDataSaved, setIsDataSaved] = useState(false);
  
  // Use ref to prevent auto-save loop
  const autoSaveAttempted = useRef(false);

  useEffect(() => {
    calculateResults();
  }, []);

  // Optimized auto-save logic to prevent loops
  useEffect(() => {
    if (results && !submissionId && !isDataSaved && !isSubmitting && !autoSaveAttempted.current) {
      console.log('Auto-saving quickscan data to Supabase...');
      autoSaveAttempted.current = true;
      handleAutoSave();
    }
  }, [results, submissionId, isDataSaved, isSubmitting]);

  const handleAutoSave = async () => {
    if (!results) return;
    
    try {
      // Only auto-save without contact details to preserve data integrity
      // Company name and email will be added when user downloads PDF
      const newSubmissionId = await submitQuickscanData(data, results);
      setSubmissionId?.(newSubmissionId);
      setIsDataSaved(true);
      console.log('Quickscan data auto-saved successfully');
    } catch (error) {
      console.error('Failed to auto-save quickscan data:', error);
      autoSaveAttempted.current = false; // Allow retry on error
    }
  };

  const handleDownload = async () => {
    if (!results) return;

    // Check if we have contact info
    if (!email || !companyName) {
      setShowLeadForm(true);
      return;
    }

    try {
      // First save/update the data if needed
      let currentSubmissionId = submissionId;
      
      if (email && companyName) {
        if (!submissionId) {
          // Save with contact details if no submission exists yet
          console.log('Saving submission with contact details...');
          const newSubmissionId = await submitQuickscanData(data, results, email, companyName);
          setSubmissionId?.(newSubmissionId);
          currentSubmissionId = newSubmissionId;
          setIsDataSaved(true);
        } else {
          // Contactgegevens aanvullen op een bestaande inzending. Dit was een
          // rechtstreekse `.update()`, maar `anon` heeft geen UPDATE-policy: die
          // update raakte nul rijen en meldde succes, dus e-mailadres en bedrijf
          // kwamen nooit aan (F8.8). De edge-functie doet het nu, en maakt
          // meteen het contact, de organisatie en de lead.
          console.log('Contactgegevens aanvullen op bestaande inzending...');
          await saveContactDetails(submissionId, email, companyName);
          currentSubmissionId = submissionId;
        }
      }

      // Generate and download professional PDF
      console.log('Generating professional PDF...');
      await generateProfessionalPDF(
        data,
        results,
        { email, companyName },
        currentSubmissionId
      );
      
      toast({
        title: "PDF gegenereerd!",
        description: "Je quickscan rapport wordt gedownload.",
      });
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "Fout bij PDF generatie",
        description: "Er ging iets mis bij het genereren van je rapport. Probeer het opnieuw.",
        variant: "destructive"
      });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (!results) {
    return (
      <div className="text-center">
        <div className="animate-energy-pulse">
          <div className="w-16 h-16 mx-auto bg-gradient-to-br from-energy-green to-energy-blue rounded-full flex items-center justify-center mb-4">
            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
        <p className="text-lg text-gray-600">Berekenen van je energiepotentieel...</p>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-4xl mx-auto">
        {/* Data saved indicator */}
        {submissionId && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6 flex items-center text-green-800">
            <CheckCircle className="w-5 h-5 mr-2" />
            <span className="text-sm">Je quickscan resultaten zijn veilig opgeslagen!</span>
          </div>
        )}

        {/* Header with logo */}
        <div className="text-center mb-8">
          <Logo className="h-12 md:h-16 mx-auto mb-6 object-contain" />
          <h2 className="text-4xl font-bold text-energy-green mb-2">
            Jouw Energie Quickscan Resultaat
          </h2>
          <p className="text-xl text-gray-600">
            Ontdek je potentieel voor energiebesparing en netonafhankelijkheid
          </p>
        </div>

        {/* 3D Energy Potential Score */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 mb-8 text-center">
          <h3 className="text-2xl font-bold mb-6">Jouw Energiepotentieel Score</h3>
          
          <div className="flex justify-center mb-6">
            <EnergyMeter3D score={results.energyPotentialScore} size={240} />
          </div>
          
          <p className="text-lg text-gray-700">
            {results.energyPotentialScore >= 80 ? 'Uitstekende' : 
             results.energyPotentialScore >= 60 ? 'Goede' : 
             results.energyPotentialScore >= 40 ? 'Redelijke' : 'Basis'} 
            mogelijkheden voor energieoptimalisatie!
          </p>
        </div>

        {/* Enhanced Key Metrics with subtle animations */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center transform hover:scale-105 transition-all duration-200 hover:shadow-md">
            <Euro className="w-12 h-12 text-energy-green mx-auto mb-4" />
            <div className="text-3xl font-bold text-energy-green mb-2 animate-slide-up">
              {formatCurrency(results.yearlyAnnualSavings)}
            </div>
            <div className="text-gray-600">Potentiële besparing per jaar</div>
            <div className="text-sm text-energy-green/70 mt-2">
              ≈ {formatCurrency(Math.round(results.yearlyAnnualSavings / 12))} per maand
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center transform hover:scale-105 transition-all duration-200 hover:shadow-md">
            <Leaf className="w-12 h-12 text-energy-green mx-auto mb-4" />
            <div className="text-3xl font-bold text-energy-green mb-2 animate-slide-up">
              {results.co2Reduction} ton
            </div>
            <div className="text-gray-600">CO2 reductie per jaar</div>
            <div className="text-sm text-energy-green/70 mt-2">
              = {Math.round((results.co2Reduction * 1000) / TREE_CO2_UPTAKE_KG_YEAR)} bomen planten
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center transform hover:scale-105 transition-all duration-200 hover:shadow-md">
            <Calendar className="w-12 h-12 text-energy-blue mx-auto mb-4" />
            <div className="text-3xl font-bold text-energy-blue mb-2 animate-slide-up">
              {results.roiMonths} maanden
            </div>
            <div className="text-gray-600">Terugverdientijd</div>
            <div className="text-sm text-energy-blue/70 mt-2">
              ROI: {Math.round(1200 / results.roiMonths)}% per jaar
            </div>
          </div>
        </div>

        {/* Benchmark Comparison */}
        <div className="mb-8">
          <ComparisonIndicator 
            score={results.energyPotentialScore}
            businessType={data.businessType}
            employeeCount={data.employeeCount}
          />
        </div>

        {/* Enhanced Recommendations */}
        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 mb-8">
          <h3 className="text-2xl font-bold mb-6 text-center">Aanbevelingen voor jouw bedrijf</h3>
          <div className="space-y-4">
            {results.recommendations.map((recommendation, index) => (
              <div key={index} className="flex items-center p-4 bg-energy-green/5 rounded-lg hover:bg-energy-green/10 transition-colors duration-200 animate-slide-up" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="w-8 h-8 bg-energy-green text-white rounded-full flex items-center justify-center mr-4 text-sm font-bold">
                  {index + 1}
                </div>
                <span className="text-gray-800">{recommendation}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Enhanced Lead Capture with PDF Generation - FIXED TEXT COLOR */}
        <div className="bg-gradient-to-r from-energy-green to-energy-blue rounded-xl p-8 text-center relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 bg-gradient-to-r from-energy-green/10 to-energy-blue/10"></div>
          
          <div className="relative z-10">
            <h3 className="text-2xl font-bold mb-4 text-white">Download je volledige rapport</h3>
            <p className="text-lg mb-6 text-white/90">
              Krijg een gedetailleerde PDF-analyse van je energiepotentieel
            </p>
            
            {showLeadForm ? (
              <div className="max-w-md mx-auto space-y-4">
                <div>
                  <Label htmlFor="email" className="text-white block text-left mb-2">E-mailadres</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 bg-white text-gray-900 placeholder-gray-500"
                    placeholder="je@bedrijf.nl"
                  />
                </div>
                <div>
                  <Label htmlFor="company" className="text-white block text-left mb-2">Bedrijfsnaam</Label>
                  <Input
                    id="company"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="mt-1 bg-white text-gray-900 placeholder-gray-500"
                    placeholder="Jouw Bedrijf B.V."
                  />
                </div>
                <Button 
                  onClick={handleDownload}
                  className="w-full bg-white text-energy-green hover:bg-gray-100 transform hover:scale-105 transition-all duration-200"
                  disabled={!email || !companyName || isSubmitting || isGenerating || isProfessionalGenerating}
                >
                  {isSubmitting || isGenerating || isProfessionalGenerating ? (
                    <div className="w-4 h-4 border-2 border-energy-green border-t-transparent rounded-full animate-spin mr-2"></div>
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  {isProfessionalGenerating ? 'PDF Genereren...' : isGenerating ? 'PDF Genereren...' : isSubmitting ? 'Opslaan...' : 'Download PDF Rapport'}
                </Button>
              </div>
            ) : (
              <Button 
                onClick={() => setShowLeadForm(true)}
                size="lg"
                className="bg-white text-energy-green hover:bg-gray-100 px-8 transform hover:scale-105 transition-all duration-200"
              >
                <Download className="w-5 h-5 mr-2" />
                Download PDF Rapport
              </Button>
            )}

            {(submissionError || generationError || professionalError) && (
              <div className="mt-4 text-red-200 text-sm bg-red-900/20 rounded-lg p-3">
                <strong>Fout:</strong> {submissionError || generationError || professionalError}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Social Proof Widget */}
      <SocialProofWidget />
    </>
  );
};
